import type { ContainerPreset } from '@/constants/componentPresets';
import type { ColorType } from '@/theme/colors';
import type { ButtonPresetConfig } from './buttonPresetConfigs';
import type { ButtonVisualState } from './buttonVisualState';

interface ResolveButtonContainerVisualPropsInput {
  config: ButtonPresetConfig;
  containerPreset?: ContainerPreset;
  backgroundOpacity?: number;
  borderColor?: ColorType;
  borderOpacity?: number;
  backdropBlur?: number;
  visualState: ButtonVisualState;
}

export interface ButtonContainerVisualProps {
  backgroundColor: ColorType | undefined;
  backgroundOpacity: number | undefined;
  borderColor: ColorType | undefined;
  borderOpacity: number | undefined;
  borderWidth: number | undefined;
  backdropBlur: number | undefined;
  glow: number | undefined;
  glowColor: ColorType | undefined;
  boxShadow: string | undefined;
}

interface ResolveContainerPresetValueInput<TValue> {
  containerPreset?: ContainerPreset;
  value: TValue;
}

interface ResolveOverrideableContainerValueInput<TValue> extends ResolveContainerPresetValueInput<TValue> {
  override?: TValue;
}

function resolveContainerPresetValue<TValue>({
  containerPreset,
  value,
}: ResolveContainerPresetValueInput<TValue>): TValue | undefined {
  if (containerPreset) {
    return undefined;
  }

  return value;
}

function resolveOverrideableContainerValue<TValue>({
  override,
  containerPreset,
  value,
}: ResolveOverrideableContainerValueInput<TValue>): TValue | undefined {
  if (override != null) {
    return override;
  }

  return resolveContainerPresetValue({ containerPreset, value });
}

function resolveContainerGlow({
  containerPreset,
  visualState,
}: Pick<ResolveButtonContainerVisualPropsInput, 'containerPreset' | 'visualState'>): number | undefined {
  if (containerPreset) {
    return undefined;
  }

  if (visualState.shadow) {
    return 0;
  }

  return visualState.glow;
}

export function resolveButtonContainerVisualProps({
  config,
  containerPreset,
  backgroundOpacity,
  borderColor,
  borderOpacity,
  backdropBlur,
  visualState,
}: ResolveButtonContainerVisualPropsInput): ButtonContainerVisualProps {
  return {
    backgroundColor: resolveContainerPresetValue({ containerPreset, value: config.bgColor }),
    backgroundOpacity: resolveOverrideableContainerValue({
      override: backgroundOpacity,
      containerPreset,
      value: visualState.backgroundOpacity,
    }),
    borderColor: resolveOverrideableContainerValue({
      override: borderColor,
      containerPreset,
      value: config.borderColor,
    }),
    borderOpacity: resolveOverrideableContainerValue({
      override: borderOpacity,
      containerPreset,
      value: visualState.borderOpacity,
    }),
    borderWidth: resolveContainerPresetValue({ containerPreset, value: config.borderWidth }),
    backdropBlur: resolveOverrideableContainerValue({
      override: backdropBlur,
      containerPreset,
      value: visualState.backdropBlur,
    }),
    glow: resolveContainerGlow({ containerPreset, visualState }),
    glowColor: resolveContainerPresetValue({ containerPreset, value: visualState.glowColor }),
    boxShadow: resolveContainerPresetValue({ containerPreset, value: visualState.shadow }),
  };
}
